import type { Message } from '@/features/chat/chat.api';

/**
 * Shared formatting for a "quoted message" preview — used by both the
 * Composer's reply banner and `MessageBubble`'s in-bubble quote block, so
 * the two surfaces never drift on truncation length or the media/location
 * placeholder text.
 */

/** Max characters shown before truncating a quoted-message preview. */
const MAX_PREVIEW_CHARS = 80;

/**
 * Human label for who sent a message, for the quoted-preview UI.
 *
 * Direction-based only (no agent-display-name lookup) — both call sites
 * render inline without pulling in `useAgentsById`, and "你 / 客户" is
 * enough context to identify which bubble is being quoted.
 */
export function quotedSenderLabel(message: Message): string {
  return message.direction === 'outbound' ? '你' : '客户';
}

/**
 * One-line preview text for a quoted message: its trimmed text if present,
 * otherwise a bracketed placeholder for media/location content, truncated
 * to `MAX_PREVIEW_CHARS` with an ellipsis.
 */
export function quotedPreviewText(message: Message): string {
  const text = message.content_text?.trim();
  if (text) {
    return text.length > MAX_PREVIEW_CHARS
      ? `${text.slice(0, MAX_PREVIEW_CHARS)}…`
      : text;
  }
  if (message.media) {
    const isImage =
      (message.media.mime?.startsWith('image/') ?? false) ||
      message.content_type === 'image' ||
      message.content_type === 'sticker';
    if (isImage) return '[图片]';
    const isAudio =
      (message.media.mime?.startsWith('audio/') ?? false) ||
      message.content_type === 'audio';
    if (isAudio) return '[语音]';
    return '[文件]';
  }
  if (message.location) return '[位置]';
  return '[消息]';
}
